'use client'

import { GoogleOutlined } from '@ant-design/icons'
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google'
import { App, Button, Space } from 'antd'
import { useState } from 'react'
import type { LoginApi } from './login.api'
import type { GoogleLoginTexts } from './texts'

/** Props of {@link GoogleLoginButton}. */
export interface GoogleLoginButtonProps {
  /** OAuth client ID of the Google sign-in. */
  clientId: string
  api: LoginApi
  texts: GoogleLoginTexts
  /** Called after the session cookie was set. */
  onSuccess: () => void
  /** Called with `true` while the code is exchanged, and with `false` when that is over. */
  onSubmitting?: (submitting: boolean) => void
  /** Reports unexpected failures, e.g. to error tracking. */
  onError?: (error: unknown) => void
  /**
   * Replaces the click handler; it gets the function that opens Google's popup, e.g. to save
   * a form first.
   */
  onClick?: (openPopup: () => void) => void
  disabled?: boolean
}

type ButtonProps = Omit<GoogleLoginButtonProps, 'clientId'>

function GoogleButton({
  api,
  texts,
  onSuccess,
  onSubmitting,
  onError,
  onClick,
  disabled,
}: ButtonProps) {
  const { message } = App.useApp()

  const login = useGoogleLogin({
    flow: 'auth-code',
    onError: (error) => {
      onError?.(error)
      void message.warning(error.error_description || texts.unavailable)
    },
    onSuccess: ({ code }) => {
      onSubmitting?.(true)
      void api.loginWithGoogle({ code }).then((result) => {
        onSubmitting?.(false)
        if (!result.ok) {
          onError?.(result.errors)
          void message.error(texts.failed)
          return
        }
        void message.success(texts.loggedIn)
        onSuccess()
      })
    },
  })

  return (
    <Button
      size="large"
      block
      disabled={disabled}
      onClick={onClick ? () => onClick(() => login()) : () => login()}
    >
      <Space>
        <GoogleOutlined />
        <span>{texts.label}</span>
      </Space>
    </Button>
  )
}

/**
 * "Sign in with Google": opens Google's popup (`auth-code` flow) and trades the code at the
 * login route `google` for a session. Renders nothing when Google's script cannot be loaded,
 * e.g. because a content blocker stops it.
 */
export function GoogleLoginButton({ clientId, ...props }: GoogleLoginButtonProps) {
  const [scriptError, setScriptError] = useState(false)

  if (scriptError) return null

  return (
    <GoogleOAuthProvider clientId={clientId} onScriptLoadError={() => setScriptError(true)}>
      <GoogleButton {...props} />
    </GoogleOAuthProvider>
  )
}
